import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { PDFExport } from '@progress/kendo-react-pdf';

import Spinner from 'components/Spinner';
import DownloadIcon from 'components/Icons/DownloadIcon';
import bloxBiService from 'services/bloxBiService';
import { getSelectedDashboardScenario, getTimeFilter, getDashboard } from 'redux/DashboardSliceV2';

import Preview from './Preview';
import { PageInstruments } from './Instruments/proptypes';
import './pdf-styles.css'; // You'll need to create this file

interface PreviewModalProperties {
  isOpen: boolean;
  onClose: () => void;
  selectedPages: any[];
}

const PreviewModal = ({ isOpen, onClose, selectedPages }: PreviewModalProperties) => {
  const dashboard = useSelector(getDashboard);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const timeFilter = useSelector(getTimeFilter);
  const targetReference = useRef<any>();
  const [pdfExport, setPDFExport] = useState(false);
  const [allPageInstruments, setAllPageInstruments] = useState<PageInstruments>({});
  const [allInstrumentsData, setAllInstrumentsData] = useState<PageInstruments>({});
  const [isPageDataLoading, setIsPageDataLoading] = useState(false);
  const [isPageInstrumentsLoading, setIsPageInstrumentsLoading] = useState(false);
  const [fetchKey, setFetchKey] = useState(0); // Add this to force re-fetch

  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const dashboardTimeFilter = useSelector(getTimeFilter);
  const contentLoading = isPageDataLoading || isPageInstrumentsLoading || pdfExport;
  const previewContentLoading = isPageDataLoading || isPageInstrumentsLoading;

  useEffect(() => {
    // Reset state when modal opens and force fresh fetch
    if (isOpen) {
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(false);
      setIsPageInstrumentsLoading(false);
      setFetchKey((previous) => previous + 1); // Force new fetch cycle
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !dashboardTimeFilter?.start ||
        !dashboardTimeFilter?.end ||
        !selectedPages?.length ||
        !selectedDashboardScenario ||
        !isOpen
      ) {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
        return;
      }

      // Reset data first, then start loading
      setAllPageInstruments({});
      setAllInstrumentsData({});
      setIsPageDataLoading(true);
      setIsPageInstrumentsLoading(true);

      try {
        const response = await bloxBiService.dashboard_full_export_pdf(
          dashboard?.id,
          dashboardTimeFilter.start,
          dashboardTimeFilter.end,
          selectedDashboardScenario?.id,
          dashboard?.model_id,
        );
        const instrumentDataMap: any = {};
        const instrumentsMap: any = {};
        response.data.pages.forEach((page: any) => {
          instrumentsMap[page.page_id] = page.metadata.instruments;
          instrumentDataMap[page.page_id] = page.data_values;
        });

        setAllPageInstruments(instrumentsMap);
        setIsPageDataLoading(false);

        setAllInstrumentsData(instrumentDataMap);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Reset to empty state on error
        setAllPageInstruments({});
        setAllInstrumentsData({});
      } finally {
        setIsPageDataLoading(false);
        setIsPageInstrumentsLoading(false);
      }
    };

    if (isOpen && fetchKey > 0) {
      fetchData();
    }
  }, [selectedPages, selectedDashboardScenario, dashboardTimeFilter, isOpen, fetchKey]);

  if (!dashboard || !selectedScenario || !timeFilter) {
    return null;
  }

  const onExport = () => {
    setPDFExport(true);
    try {
      if (targetReference.current) {
        setTimeout(() => {
          targetReference.current.save();
          setPDFExport(false);
        }, 1000);
      }
    } catch {
      setTimeout(() => {
        setPDFExport(false);
      }, 1000);
      // alert('Export Failed  !!!');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent minW={'calc(210mm + 100px)'} maxW={'90vw'}>
        <ModalHeader>
          <Flex justifyContent='end' alignItems='center'>
            <Box>
              <Button
                variant={'save_button'}
                size='sm'
                mr={3}
                leftIcon={contentLoading ? <Spinner size='md' /> : <DownloadIcon />}
                isLoading={contentLoading}
                disabled={contentLoading}
                loadingText='Exporting'
                backgroundColor='#5A58AB'
                color='white'
                onClick={() => {
                  setTimeout(() => {
                    onExport();
                  }, 1000);
                }}
              >
                Export
              </Button>
              <Button
                size='sm'
                variant={'cancel_button'}
                onClick={onClose}
                leftIcon={contentLoading ? <Spinner size='md' /> : <></>}
                isLoading={contentLoading}
                disabled={contentLoading}
              >
                Cancel
              </Button>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalBody className='pdf-preview-container scroll' maxH={'75vh'} overflow={'scroll'}>
          <PDFExport
            paperSize='a4'
            margin='0.1cm'
            ref={targetReference}
            fileName={`${dashboard?.name} .pdf`}
            author='BLOX'
            title={`${dashboard?.name} Dashboard Export`}
            subject='Dashboard Export'
            creator='BLOX'
            date={new Date()}
            keepTogether='page'
            landscape={false}
            scale={0.4}
            forcePageBreak='.page-break'
          >
            <Box className='a4-preview-wrapper'>
              {/* <Box className='a4-page header-page' mb={5}>
                <Text fontSize='28px' fontWeight='bold' color='black'>
                  {dashboard?.name}
                </Text>
                <Flex>
                  <Box>
                    <Text fontSize='md' color={'black'} cursor='pointer' noOfLines={1}>
                      {`Scenario: ${selectedScenario?.name || 'No scenario selected'} | Period: ${
                        timeFilter?.start || 'N/A'
                      } - ${timeFilter?.end || 'N/A'}`}
                    </Text>
                  </Box>
                </Flex>
              </Box> */}
              {previewContentLoading && (
                <>
                  <Text
                    color='black'
                    height={'10vw'}
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    Loading preview...
                  </Text>
                </>
              )}
              {!previewContentLoading && (
                <Preview
                  selectedPages={selectedPages}
                  allPageInstruments={allPageInstruments}
                  allInstrumentsData={allInstrumentsData}
                  isPageDataLoading={isPageDataLoading}
                  isPageInstrumentsLoading={isPageInstrumentsLoading}
                />
              )}
            </Box>
          </PDFExport>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PreviewModal;
